import React from 'react';
import { Grid, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import RemoveSharpIcon from '@mui/icons-material/RemoveSharp';
import DateRangeSharpIcon from '@mui/icons-material/DateRangeSharp';
import PropTypes from 'prop-types';
import RoleDropdown from '~/modules/projects/tasks/components/RoleDropdown';
import useTaskResourceEstimateChangeHandlers from './useTaskResourceEstimateChangeHandlers';
import EstimatedHoursEditor from './EstimatedHoursEditor';
import TaskResourceDropdown from './TaskResourceDropdown';

const removeIcon = <RemoveSharpIcon />;
const dateRangeIcon = <DateRangeSharpIcon />;

const noop = () => {};

const useStyles = makeStyles(theme => ({
  cell: {
    borderBottom: `1px solid ${theme.palette.table.border}`,
    borderRight: `1px solid ${theme.palette.table.border}`
  },
  removeIconCell: {
    borderBottom: `1px solid ${theme.palette.table.border}`,
    color: theme.palette.text.secondary
  },
  estimatedHours: {
    fontWeight: theme.typography.fontWeightBold
  },
  estimated: {
    height: '100%'
  },
  enterDates: {
    color: theme.palette.text.secondary,
    textTransform: 'capitalize',
    height: '100%'
  },
  removeIcon: {
    color: theme.palette.text.secondary
  },
  container: {
    marginBottom: 0,
    marginTop: 0
  }
}));

export const TaskResourceEstimateRow = ({
  resourceEstimate,
  onResourceEstimateChange,
  taskEstimatedCost,
  projectSlug,
  selectedResources
}) => {
  const classes = useStyles();

  const { formatMessage } = useIntl();

  const {
    onRemove,
    onEstimatedHoursChange,
    onRoleChange,
    onResourceChange
  } = useTaskResourceEstimateChangeHandlers({
    onResourceEstimateChange,
    resourceEstimate
  });

  return (
    <Grid key={resourceEstimate.rowId} container className={classes.container}>
      <Grid item xs={12} sm={3} className={classes.cell}>
        <RoleDropdown
          value={resourceEstimate.role || null}
          onChange={onRoleChange}
          variant="standard"
          placeholder={formatMessage({
            id: 'taskResourceAssignments.selectARole'
          })}
          projectSlug={projectSlug}
        />
      </Grid>
      <Grid item xs={12} sm={3} className={classes.cell}>
        <TaskResourceDropdown
          assignedRole={resourceEstimate.role}
          onResourceChange={onResourceChange}
          projectSlug={projectSlug}
          value={resourceEstimate.resource}
          selectedResources={selectedResources}
          placeholder={formatMessage({
            id: 'taskResourceAssignments.selectAResource'
          })}
        />
      </Grid>
      <Grid item xs={12} sm={2} className={classes.cell}>
        <div className={classes.estimated}>
          <EstimatedHoursEditor
            onEstimatedHoursChange={onEstimatedHoursChange}
            cost={resourceEstimate.estimatedCost}
            taskEstimatedCost={taskEstimatedCost}
            estimatedHours={resourceEstimate.estimatedHours}
          />
        </div>
      </Grid>
      <Grid item sm={3} className={classes.cell}>
        <Button
          className={classes.enterDates}
          startIcon={dateRangeIcon}
          onClick={noop}
        >
          <Typography variant="body2">
            {formatMessage({ id: 'taskResourceAssignments.enterDates' })}
          </Typography>
        </Button>
      </Grid>
      <Grid item xs={12} sm={1} className={classes.removeIconCell}>
        <Button
          className={classes.removeIcon}
          aria-label={formatMessage({
            id: 'button.delete'
          })}
          onClick={onRemove}
        >
          {removeIcon}
        </Button>
      </Grid>
    </Grid>
  );
};

TaskResourceEstimateRow.propTypes = {
  resourceEstimate: PropTypes.object.isRequired,
  taskEstimatedCost: PropTypes.object,
  onResourceEstimateChange: PropTypes.func,
  projectSlug: PropTypes.string,
  selectedResources: PropTypes.array
};

export default TaskResourceEstimateRow;
